import { useEffect, useState, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Sparkles, Check, ChevronDown, Loader2, Star, Search, ShieldCheck, Plus,
  Building2, CalendarDays, Utensils, CalendarCheck, Tag, Megaphone, Briefcase, Trophy,
  Mic2, ShoppingBag, Upload, FerrisWheel, Gamepad2, Ticket, Clock, Handshake,
  Sailboat, CreditCard, MapPin, Shield, TrendingUp, Heart, Map, Radio, ArrowRight,
  Store, Gift, QrCode, Music, Disc3, Users, Sun, Smartphone, Info, Compass, ClipboardList,
} from "lucide-react";
import { api, errMsg } from "../lib/api";
import { entLabel, ComparisonTable } from "./Pricing";
import { MascotHero, MascotSays } from "../components/sales/MascotHost";

const ICONS = {
  Building2, CalendarDays, Utensils, CalendarCheck, Tag, Megaphone, Briefcase, Trophy, Mic2,
  ShoppingBag, Upload, Star, FerrisWheel, Gamepad2, Ticket, Clock, Handshake, Sailboat,
  CreditCard, MapPin, Shield, TrendingUp, Heart, Map, Radio, Search, ShieldCheck, Plus, Sparkles,
  Store, Gift, QrCode, Music, Disc3, Users, Sun, Smartphone, Info, Compass, ClipboardList,
};
const Icon = ({ name, ...p }) => { const C = ICONS[name] || Sparkles; return <C {...p} />; };

const TAG_STYLES = {
  Included: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  Premium: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  VIP: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  "Coming Soon": "bg-sky-500/15 text-sky-300 border-sky-500/30",
  "Admin Only": "bg-zinc-500/15 text-zinc-400 border-zinc-500/30",
};

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.55, ease: "easeOut" },
};

let gaLoaded = false;
function loadGA(id) {
  if (!id || gaLoaded) return;
  gaLoaded = true;
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () { window.dataLayer.push(arguments); };
  window.gtag("js", new Date());
  window.gtag("config", id);
}

function useTrack(slug) {
  return useCallback((event_type, extra = {}) => {
    api.post("/track", { event_type, page: slug, source: slug, ...extra }).catch(() => {});
    if (window.gtag) window.gtag("event", event_type, { page: slug, ...extra });
  }, [slug]);
}

function SEO({ seo, slug, title }) {
  useEffect(() => {
    document.title = seo?.title || title || "Gulf Coast VIP";
    const set = (attr, key, val) => {
      if (!val) return;
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.setAttribute("content", val);
    };
    set("name", "description", seo?.description);
    set("property", "og:title", seo?.og_title || seo?.title);
    set("property", "og:description", seo?.og_description || seo?.description);
    set("property", "og:image", seo?.og_image);
    set("property", "og:url", `${window.location.origin}/${slug}`);
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) { link = document.createElement("link"); link.setAttribute("rel", "canonical"); document.head.appendChild(link); }
    link.setAttribute("href", `${window.location.origin}/${slug}`);
    return () => { document.title = "Gulf Coast VIP"; };
  }, [seo, slug, title]);
  return null;
}

const CtaLink = ({ btn, track, kind = "cta_click", className, testid }) => (
  <Link to={btn.href || "/"} onClick={() => track(kind, { target: btn.href, meta: btn.label })}
    data-testid={testid} className={className}>{btn.label}</Link>
);

function heroVideo(url) {
  if (!url) return null;
  const yt = String(url).match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([\w-]{6,})/);
  if (yt) return { type: "iframe", src: `https://www.youtube.com/embed/${yt[1]}?autoplay=1&mute=1&loop=1&playlist=${yt[1]}&controls=0&rel=0&playsinline=1&modestbranding=1` };
  const vm = String(url).match(/vimeo\.com\/(\d+)/);
  if (vm) return { type: "iframe", src: `https://player.vimeo.com/video/${vm[1]}?autoplay=1&muted=1&loop=1&background=1` };
  return { type: "video", src: url };
}

function Hero({ hero, track, slug }) {
  const vid = heroVideo(hero.video_url);
  return (
    <section className="relative min-h-[82vh] flex items-end overflow-hidden" data-testid={`sales-hero-${slug}`}>
      <div className="absolute inset-0">
        {hero.image && <img src={hero.image} alt={hero.heading} className="w-full h-full object-cover" fetchPriority="high" />}
        {vid?.type === "video" && (
          <video src={vid.src} autoPlay muted loop playsInline preload="auto"
            ref={(el) => { if (el) { el.muted = true; el.play().catch(() => {}); } }}
            poster={hero.image || undefined} data-testid="hero-video"
            className="absolute inset-0 w-full h-full object-cover" />
        )}
        {vid?.type === "iframe" && (
          <iframe src={vid.src} title="Promo video" data-testid="hero-video-iframe"
            allow="autoplay; encrypted-media" frameBorder="0"
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[177.78vh] min-w-full h-[56.25vw] min-h-full pointer-events-none" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/55 to-[#0a0a0a]/10" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a]/70 via-transparent to-transparent" />
      </div>
      <div className="relative z-10 w-full px-6 md:px-12 lg:px-20 pb-16 pt-40">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-3xl">
          {hero.badge_text && (
            <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.2em] text-teal-300 bg-teal-500/10 border border-teal-500/30 rounded-full px-4 py-1.5 mb-5">
              <Sparkles size={12} /> {hero.badge_text}
            </span>
          )}
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">{hero.heading}</h1>
          <p className="text-zinc-300 text-base md:text-lg mt-5 max-w-2xl">{hero.subheading}</p>
          <div className="flex flex-wrap gap-3 mt-8">
            {hero.cta_primary?.label && (
              <CtaLink btn={hero.cta_primary} track={track} testid="hero-cta-primary"
                className="bg-rose-500 hover:bg-rose-600 text-white font-bold px-7 py-3.5 rounded-full text-sm transition-transform hover:scale-[1.03] shadow-xl shadow-rose-500/25" />
            )}
            {hero.cta_secondary?.label && (
              <CtaLink btn={hero.cta_secondary} track={track} testid="hero-cta-secondary"
                className="border border-white/25 hover:border-white/60 text-white font-bold px-7 py-3.5 rounded-full text-sm transition-colors backdrop-blur" />
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

const Wrap = ({ children, alt }) => (
  <section className={`px-6 md:px-12 lg:px-20 py-16 md:py-24 ${alt ? "bg-[#0d0f10]" : ""}`}>{children}</section>
);
const H2 = ({ children }) => <h2 className="font-display font-extrabold text-2xl md:text-3xl max-w-2xl">{children}</h2>;
const Sub = ({ children }) => children ? <p className="text-zinc-400 mt-3 max-w-2xl">{children}</p> : null;

function WhoFor({ s }) {
  return (
    <Wrap>
      <motion.div {...fadeUp}>
        <p className="text-teal-300 text-xs font-bold uppercase tracking-[0.2em] mb-3">Who it's for</p>
        <H2>{s.heading}</H2><Sub>{s.text}</Sub>
        <div className="flex flex-wrap gap-2.5 mt-7">
          {(s.chips || []).map((c) => (
            <span key={c} className="text-sm border border-white/15 bg-white/[0.04] rounded-full px-4 py-2 text-zinc-200">{c}</span>
          ))}
        </div>
      </motion.div>
    </Wrap>
  );
}

function Benefits({ s }) {
  return (
    <Wrap alt>
      <motion.div {...fadeUp}><H2>{s.heading}</H2><Sub>{s.sub}</Sub></motion.div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
        {(s.items || []).map((b, i) => (
          <motion.div key={b.title || i} {...fadeUp} transition={{ duration: 0.5, delay: (i % 4) * 0.07 }}
            className="bg-[#131516] border border-white/10 rounded-2xl p-6 hover:border-teal-500/40 transition-colors group">
            <div className="w-11 h-11 rounded-xl bg-teal-500/10 border border-teal-500/25 flex items-center justify-center text-teal-300 mb-4 group-hover:scale-110 transition-transform">
              <Icon name={b.icon} size={20} />
            </div>
            <h3 className="font-bold text-base md:text-lg">{b.title}</h3>
            <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{b.text}</p>
          </motion.div>
        ))}
      </div>
    </Wrap>
  );
}

function Features({ s }) {
  return (
    <Wrap>
      <motion.div {...fadeUp}><H2>{s.heading}</H2><Sub>{s.sub}</Sub></motion.div>
      <div className="space-y-10 mt-10">
        {(s.groups || []).map((g, gi) => (
          <motion.div key={g.title || gi} {...fadeUp}>
            <h3 className="font-display font-bold text-lg md:text-xl mb-4 flex items-center gap-2">
              <span className="w-8 h-[2px] bg-rose-500 inline-block" /> {g.title}
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {(g.items || []).map((f, fi) => (
                <div key={f.name || fi} className="bg-white/[0.03] border border-white/10 rounded-xl p-5 flex flex-col gap-2 hover:bg-white/[0.05] transition-colors">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-bold text-sm">{f.name}</p>
                    {f.tag && <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${TAG_STYLES[f.tag] || TAG_STYLES.Included}`}>{f.tag}</span>}
                  </div>
                  <p className="text-xs text-zinc-400 leading-relaxed">{f.text}</p>
                </div>
              ))}
            </div>
          </motion.div>
        ))}
      </div>
    </Wrap>
  );
}

function HowItWorks({ s }) {
  return (
    <Wrap alt>
      <motion.div {...fadeUp}><H2>{s.heading}</H2></motion.div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
        {(s.steps || []).map((st, i) => (
          <motion.div key={st.title || i} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.08 }} className="relative bg-[#131516] border border-white/10 rounded-2xl p-6">
            <span className="font-display font-extrabold text-4xl text-teal-400/25">{String(i + 1).padStart(2, "0")}</span>
            <h3 className="font-bold mt-2">{st.title}</h3>
            <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{st.text}</p>
          </motion.div>
        ))}
      </div>
    </Wrap>
  );
}

function Preview({ s }) {
  return (
    <Wrap>
      <div className="grid lg:grid-cols-2 gap-10 items-center">
        <motion.div {...fadeUp}>
          <p className="text-teal-300 text-xs font-bold uppercase tracking-[0.2em] mb-3">Your dashboard</p>
          <H2>{s.heading}</H2><Sub>{s.text}</Sub>
          <ul className="mt-6 space-y-3">
            {(s.bullets || []).map((b, i) => (
              <li key={`${i}-${b}`} className="flex items-start gap-3 text-sm text-zinc-300">
                <Check size={16} className="text-emerald-400 mt-0.5 shrink-0" /> {b}
              </li>
            ))}
          </ul>
        </motion.div>
        <motion.div {...fadeUp} className="bg-gradient-to-br from-teal-500/10 to-rose-500/10 border border-white/10 rounded-3xl p-8 lg:p-10">
          {s.image ? <img src={s.image} alt={s.heading} loading="lazy" className="rounded-2xl w-full" /> : (
            <div className="space-y-3" aria-hidden="true">
              {[80, 62, 71, 55].map((w, i) => (
                <div key={`${i}-${w}`} className="bg-[#131516] border border-white/10 rounded-xl p-4 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-white/10" />
                  <div className="flex-1"><div className="h-2.5 rounded bg-white/15" style={{ width: `${w}%` }} /><div className="h-2 rounded bg-white/8 mt-2" style={{ width: `${w - 20}%` }} /></div>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </Wrap>
  );
}

function Showcase({ s, track }) {
  const portrait = !!s.portrait;
  return (
    <Wrap>
      <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center" data-testid="showcase-section">
        <motion.div {...fadeUp} className={s.flip ? "lg:order-2" : ""}>
          {s.eyebrow && <p className="text-teal-300 text-xs font-bold uppercase tracking-[0.2em] mb-3">{s.eyebrow}</p>}
          <H2>{s.heading}</H2><Sub>{s.text}</Sub>
          {(s.bullets || []).length > 0 && (
            <ul className="mt-6 space-y-3">
              {s.bullets.map((b, i) => (
                <li key={`${i}-${b}`} className="flex items-start gap-3 text-sm text-zinc-300">
                  <Check size={16} className="text-emerald-400 mt-0.5 shrink-0" /> {b}
                </li>
              ))}
            </ul>
          )}
          {s.cta?.label && (
            <CtaLink btn={s.cta} track={track} testid="showcase-cta"
              className="inline-flex items-center gap-2 mt-7 text-sm font-bold text-rose-400 hover:text-rose-300" />
          )}
        </motion.div>
        <motion.div {...fadeUp} className={s.flip ? "lg:order-1" : ""}>
          <div className="relative">
            <div className="absolute -inset-6 bg-gradient-to-br from-rose-500/15 to-teal-500/15 rounded-[2rem] blur-2xl" aria-hidden="true" />
            {s.image ? (
              <img src={s.image} alt={s.heading} loading="lazy"
                className={`relative rounded-2xl border border-white/10 shadow-2xl w-full ${portrait ? "max-h-[560px] object-contain mx-auto lg:max-w-md" : ""}`} />
            ) : (
              <div className="relative bg-[#131516] border border-white/10 rounded-2xl p-8 space-y-3" aria-hidden="true">
                {[80, 62, 71, 55].map((w, i) => (
                  <div key={`${i}-${w}`} className="bg-[#0f1112] border border-white/10 rounded-xl p-4 flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-white/10" />
                    <div className="flex-1"><div className="h-2.5 rounded bg-white/15" style={{ width: `${w}%` }} /><div className="h-2 rounded bg-white/8 mt-2" style={{ width: `${w - 20}%` }} /></div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </Wrap>
  );
}

function Toolkit({ s, track, owner }) {
  const eyebrow = s.eyebrow || (owner ? `${owner.charAt(0).toUpperCase()}${owner.slice(1)} Toolkit` : "Toolkit");
  return (
    <Wrap alt>
      <motion.div {...fadeUp}>
        <p className="text-teal-300 text-xs font-bold uppercase tracking-[0.2em] mb-3">{eyebrow}</p>
        <H2>{s.heading}</H2><Sub>{s.text}</Sub>
      </motion.div>
      <div className={`grid sm:grid-cols-2 ${(s.items || []).length === 4 ? "lg:grid-cols-4" : "lg:grid-cols-3"} gap-5 mt-10`} data-testid="toolkit-grid">
        {(s.items || []).map((t, i) => (
          <motion.div key={t.href || i} {...fadeUp} transition={{ duration: 0.5, delay: (i % 3) * 0.07 }}>
            <Link to={t.href || "/"} onClick={() => track("cta_click", { target: t.href, meta: t.title })}
              data-testid={`toolkit-card-${i}`}
              className="group flex flex-col h-full bg-[#131516] border border-white/10 rounded-2xl p-6 hover:border-rose-500/50 transition-colors">
              <div className="w-11 h-11 rounded-xl bg-rose-500/10 border border-rose-500/25 flex items-center justify-center text-rose-300 mb-4 group-hover:scale-110 transition-transform">
                <Icon name={t.icon} size={20} />
              </div>
              <h3 className="font-bold text-base md:text-lg">{t.title}</h3>
              <p className="text-sm text-zinc-400 mt-2 leading-relaxed flex-1">{t.text}</p>
              <span className="inline-flex items-center gap-1.5 mt-4 text-sm font-bold text-rose-400 group-hover:text-rose-300">
                {t.label || "Explore"} <ArrowRight size={14} />
              </span>
            </Link>
          </motion.div>
        ))}
      </div>
    </Wrap>
  );
}

function PlansSection({ s, target, track }) {
  const [plans, setPlans] = useState(null);
  const nav = useNavigate();
  useEffect(() => {
    if (!target) return;
    api.get(`/plans?target=${target}`).then((r) => { setPlans(r.data); track("plan_view"); }).catch(() => setPlans([]));
  }, [target, track]);
  if (!target) return null;
  if (plans === null) return <Wrap alt><Loader2 className="animate-spin text-zinc-500" /></Wrap>;
  if (!plans.length) return null;
  const pricingHref = `/pricing/${target}s`;
  const compareLabel = `${target.charAt(0).toUpperCase()}${target.slice(1)} plans`;
  return (
    <Wrap alt>
      <motion.div {...fadeUp}><H2>{s.heading}</H2><Sub>{s.sub}</Sub></motion.div>
      <div className={`grid gap-5 sm:grid-cols-2 ${plans.length > 3 ? "lg:grid-cols-4" : "lg:grid-cols-3"} mt-10`}>
        {plans.map((p, i) => {
          const launch = p.launch_active && Number(p.launch_price) < Number(p.regular_price);
          const price = Number(p.price);
          const vip = (p.badge || "").toLowerCase().includes("vip");
          const soon = p.status === "coming_soon";
          return (
            <motion.div key={p.id} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.06 }}
              className={`relative rounded-2xl p-6 border flex flex-col ${vip ? "border-rose-500/60 bg-rose-500/[0.06]" : "border-white/10 bg-[#131516]"}`}
              data-testid={`sales-plan-${p.key || p.id}`}>
              {p.badge && <span className={`absolute -top-3 left-5 text-[10px] font-bold px-3 py-1 rounded-full ${vip ? "bg-rose-500 text-white" : "bg-teal-500/90 text-black"}`}>{p.badge}</span>}
              <h3 className="font-display font-bold text-lg mt-1">{p.name}</h3>
              <div className="mt-3 flex items-end gap-2">
                <span className="font-display font-extrabold text-3xl">{price === 0 ? "Free" : `$${price}`}</span>
                {price > 0 && p.plan_type === "subscription" && <span className="text-xs text-zinc-500 mb-1">/mo</span>}
                {price > 0 && p.plan_type === "boost" && <span className="text-xs text-zinc-500 mb-1">one-time</span>}
              </div>
              {launch && (
                <p className="text-xs mt-1.5"><span className="text-zinc-500 line-through">${p.regular_price}</span>{" "}
                  <span className="text-emerald-300 font-bold">Launch price — save ${(Number(p.regular_price) - Number(p.launch_price)).toFixed(0)}</span></p>
              )}
              {Number(p.yearly_price) > 0 && <p className="text-xs text-zinc-500 mt-1">or ${p.yearly_price}/yr</p>}
              <p className="text-xs text-zinc-400 mt-3 leading-relaxed">{p.description}</p>
              <ul className="mt-4 space-y-1.5 flex-1" data-testid={`sales-plan-features-${p.key || p.id}`}>
                {p.features_intro && <li className="text-xs font-bold text-rose-300">{p.features_intro}</li>}
                {(() => {
                  const feats = p.features && p.features.length ? p.features : (p.entitlements || []).map(entLabel);
                  return <>
                    {feats.slice(0, 7).map((f) => (
                      <li key={f} className="flex items-center gap-2 text-xs text-zinc-300"><Check size={13} className="text-emerald-400 shrink-0" /> {f}</li>
                    ))}
                    {feats.length > 7 && <li className="text-xs text-zinc-500 pl-5">+ {feats.length - 7} more</li>}
                  </>;
                })()}
              </ul>
              {soon ? <span className="mt-5 text-center text-xs font-bold text-sky-300 border border-sky-500/30 rounded-full py-2.5">Coming Soon</span> : (
                <Link to={pricingHref} onClick={() => track("checkout_start", { target: p.name })}
                  data-testid={`sales-plan-cta-${p.key || p.id}`}
                  className={`mt-5 text-center text-sm font-bold rounded-full py-2.5 transition-colors ${vip ? "bg-rose-500 hover:bg-rose-600 text-white" : "border border-white/20 hover:border-white/50"}`}>
                  {price === 0 ? "Start Free" : "Choose Plan"}
                </Link>
              )}
            </motion.div>
          );
        })}
      </div>
      <p className="text-xs text-zinc-500 mt-6">Prices shown live from the platform — cancel anytime from your dashboard.</p>
      <ComparisonTable
        items={plans}
        billing="monthly"
        label={compareLabel}
        onBuy={(p) => { track("compare_cta", { target: p.name }); nav(pricingHref); }}
      />
    </Wrap>
  );
}

function SandySection({ s, track }) {
  return (
    <Wrap>
      <motion.div {...fadeUp} className="bg-gradient-to-r from-rose-500/10 via-[#131516] to-teal-500/10 border border-white/10 rounded-3xl p-8 md:p-12 flex flex-col md:flex-row items-start md:items-center gap-6">
        <div className="w-14 h-14 rounded-2xl bg-rose-500 flex items-center justify-center shrink-0"><Sparkles size={26} /></div>
        <div className="flex-1">
          <h2 className="font-display font-extrabold text-xl md:text-2xl">{s.heading}</h2>
          <p className="text-zinc-400 mt-2 max-w-2xl text-sm md:text-base">{s.text}</p>
        </div>
        <button onClick={() => { track("sandy_open"); document.querySelector('[data-testid="sandy-open-btn"]')?.click(); }}
          data-testid="sales-sandy-cta"
          className="bg-white text-black font-bold px-6 py-3 rounded-full text-sm hover:bg-zinc-200 transition-colors whitespace-nowrap">
          Ask Sandy
        </button>
      </motion.div>
    </Wrap>
  );
}

function ClaimPath({ s, track }) {
  return (
    <Wrap alt>
      <motion.div {...fadeUp}><H2>{s.heading}</H2><Sub>{s.text}</Sub></motion.div>
      <div className="grid sm:grid-cols-3 gap-5 mt-10">
        {(s.options || []).map((o, i) => (
          <motion.div key={o.title || i} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.08 }} className="bg-[#131516] border border-white/10 rounded-2xl p-6 flex flex-col">
            <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-teal-300 mb-4"><Icon name={o.icon} size={18} /></div>
            <h3 className="font-bold">{o.title}</h3>
            <p className="text-sm text-zinc-400 mt-2 flex-1">{o.text}</p>
            <Link to={o.href} onClick={() => track("claim_start", { target: o.href, meta: o.title })}
              className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-rose-400 hover:text-rose-300"
              data-testid={`claim-option-${i}`}>{o.label} <ArrowRight size={14} /></Link>
          </motion.div>
        ))}
      </div>
    </Wrap>
  );
}

function Faq({ s }) {
  const [open, setOpen] = useState(null);
  return (
    <Wrap>
      <motion.div {...fadeUp}><H2>{s.heading}</H2></motion.div>
      <div className="max-w-3xl mt-8 divide-y divide-white/10 border border-white/10 rounded-2xl overflow-hidden">
        {(s.items || []).map((f, i) => (
          <div key={f.q || i} className="bg-[#111]">
            <button onClick={() => setOpen(open === i ? null : i)} aria-expanded={open === i}
              data-testid={`faq-q-${i}`}
              className="w-full flex items-center justify-between gap-4 text-left px-6 py-4.5 py-4 font-bold text-sm hover:bg-white/[0.03] transition-colors">
              {f.q}<ChevronDown size={16} className={`shrink-0 transition-transform ${open === i ? "rotate-180" : ""}`} />
            </button>
            {open === i && <p className="px-6 pb-5 text-sm text-zinc-400 leading-relaxed">{f.a}</p>}
          </div>
        ))}
      </div>
    </Wrap>
  );
}

function FinalCta({ s, track }) {
  return (
    <section className="px-6 md:px-12 lg:px-20 py-20 md:py-28 text-center relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-rose-500/[0.06] to-teal-500/[0.08]" aria-hidden="true" />
      <motion.div {...fadeUp} className="relative">
        <h2 className="font-display font-extrabold text-3xl md:text-5xl max-w-3xl mx-auto leading-tight">{s.heading}</h2>
        <p className="text-zinc-400 mt-4 max-w-xl mx-auto">{s.text}</p>
        <div className="flex flex-wrap justify-center gap-3 mt-8">
          {(s.buttons || []).map((b, i) => (
            <CtaLink key={b.label || i} btn={b} track={track} testid={`final-cta-${i}`}
              className={b.style === "primary"
                ? "bg-rose-500 hover:bg-rose-600 text-white font-bold px-8 py-3.5 rounded-full text-sm transition-transform hover:scale-[1.03] shadow-xl shadow-rose-500/25"
                : "border border-white/25 hover:border-white/60 font-bold px-8 py-3.5 rounded-full text-sm transition-colors"} />
          ))}
        </div>
      </motion.div>
    </section>
  );
}

function LeadForm({ s, slug, track }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", business_name: "", city: "", state: "", link: "", message: "", preferred_contact: "email", consent: false });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!f.consent) { toast.error("Please check the consent box"); return; }
    setBusy(true);
    try {
      await api.post("/leads", { ...f, listing_type: s.listing_type, source: slug });
      track("lead_submit"); setSent(true);
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  const inp = "w-full bg-[#0f1112] border border-white/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-teal-500 transition-colors";
  if (sent) return (
    <Wrap alt><div className="max-w-xl mx-auto text-center py-10" data-testid="lead-form-success">
      <div className="w-14 h-14 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center mx-auto mb-4"><Check className="text-emerald-400" /></div>
      <h2 className="font-display font-extrabold text-2xl">Thanks — we'll be in touch!</h2>
      <p className="text-zinc-400 mt-2 text-sm">Your info is in. A real human will reach out soon.</p>
    </div></Wrap>
  );
  return (
    <Wrap alt>
      <div className="max-w-2xl mx-auto">
        <motion.div {...fadeUp} className="text-center"><H2>{s.heading}</H2><p className="text-zinc-400 mt-3">{s.text}</p></motion.div>
        <motion.form {...fadeUp} onSubmit={submit} className="mt-8 grid sm:grid-cols-2 gap-4" data-testid="lead-form">
          <div><label htmlFor="lead-name" className="text-xs text-zinc-500 block mb-1.5">Your name *</label>
            <input id="lead-name" required className={inp} value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="lead-name" /></div>
          <div><label htmlFor="lead-email" className="text-xs text-zinc-500 block mb-1.5">Email *</label>
            <input id="lead-email" type="email" required className={inp} value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="lead-email" /></div>
          <div><label htmlFor="lead-phone" className="text-xs text-zinc-500 block mb-1.5">Phone</label>
            <input id="lead-phone" className={inp} value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="lead-phone" /></div>
          <div><label htmlFor="lead-biz" className="text-xs text-zinc-500 block mb-1.5">{s.listing_type === "artist" ? "Artist / band name" : "Business name"}</label>
            <input id="lead-biz" className={inp} value={f.business_name} onChange={(e) => set("business_name", e.target.value)} data-testid="lead-business" /></div>
          <div><label htmlFor="lead-city" className="text-xs text-zinc-500 block mb-1.5">City</label>
            <input id="lead-city" className={inp} value={f.city} onChange={(e) => set("city", e.target.value)} /></div>
          <div><label htmlFor="lead-state" className="text-xs text-zinc-500 block mb-1.5">State</label>
            <select id="lead-state" className={inp} value={f.state} onChange={(e) => set("state", e.target.value)}>
              <option value="">Select…</option>
              {["Florida", "Alabama", "Mississippi", "Louisiana", "Texas"].map((st) => <option key={st}>{st}</option>)}
            </select></div>
          <div className="sm:col-span-2"><label htmlFor="lead-link" className="text-xs text-zinc-500 block mb-1.5">Website or social link</label>
            <input id="lead-link" className={inp} value={f.link} onChange={(e) => set("link", e.target.value)} /></div>
          <div className="sm:col-span-2"><label htmlFor="lead-msg" className="text-xs text-zinc-500 block mb-1.5">Message</label>
            <textarea id="lead-msg" rows={3} className={inp} value={f.message} onChange={(e) => set("message", e.target.value)} data-testid="lead-message" /></div>
          <div><label htmlFor="lead-pref" className="text-xs text-zinc-500 block mb-1.5">Preferred contact</label>
            <select id="lead-pref" className={inp} value={f.preferred_contact} onChange={(e) => set("preferred_contact", e.target.value)}>
              <option value="email">Email</option><option value="phone">Phone</option><option value="text">Text</option>
            </select></div>
          <label className="sm:col-span-2 flex items-start gap-3 text-xs text-zinc-400 cursor-pointer">
            <input type="checkbox" checked={f.consent} onChange={(e) => set("consent", e.target.checked)} className="mt-0.5 accent-rose-500" data-testid="lead-consent" />
            I agree to be contacted about Gulf Coast VIP. No spam, ever.
          </label>
          <button type="submit" disabled={busy} data-testid="lead-submit"
            className="sm:col-span-2 bg-rose-500 hover:bg-rose-600 disabled:opacity-60 font-bold py-3.5 rounded-full text-sm flex items-center justify-center gap-2">
            {busy && <Loader2 size={15} className="animate-spin" />} Send My Info
          </button>
        </motion.form>
      </div>
    </Wrap>
  );
}

const RENDER = {
  who_for: (s) => <WhoFor s={s} />,
  benefits: (s) => <Benefits s={s} />,
  features: (s) => <Features s={s} />,
  how_it_works: (s) => <HowItWorks s={s} />,
  preview: (s) => <Preview s={s} />,
  faq: (s) => <Faq s={s} />,
};

export default function SalesPage({ slug }) {
  const [page, setPage] = useState(undefined);
  const track = useTrack(slug);
  const { search } = useLocation();
  const variant = new URLSearchParams(search).get("variant") || "";

  useEffect(() => {
    window.scrollTo(0, 0);
    setPage(undefined);
    api.get(`/sales-pages/${slug}${variant ? `?variant=${variant}` : ""}`).then((r) => setPage(r.data)).catch(() => setPage(null));
    api.get("/sales-config").then((r) => loadGA(r.data.ga_measurement_id)).catch(() => {});
    localStorage.setItem("sales_source", slug);
  }, [slug, variant]);

  useEffect(() => { if (page) track("page_view"); }, [page, track]);

  if (page === undefined) return <div className="py-40 flex justify-center"><Loader2 className="animate-spin text-rose-500" /></div>;
  if (!page) return <div className="py-40 text-center text-zinc-500">This page isn't available right now.</div>;

  return (
    <div className="bg-[#0a0a0a] text-white" data-testid={`sales-page-${slug}`}>
      <SEO seo={page.seo} slug={slug} title={page.title} />
      {page.mascot ? <MascotHero hero={page.hero} mascot={page.mascot} track={track} slug={slug} />
        : <Hero hero={page.hero} track={track} slug={slug} />}
      {(page.sections || []).filter((s) => s.enabled !== false).map((s) => {
        if (s.type === "mascot_says") return <MascotSays key={s.id} s={s} mascot={page.mascot} />;
        if (s.type === "showcase") return <Showcase key={s.id} s={s} track={track} />;
        if (s.type === "toolkit") return <Toolkit key={s.id} s={s} track={track} owner={page.target} />;
        if (s.type === "plans") return <PlansSection key={s.id} s={s} target={page.plans_target} track={track} />;
        if (s.type === "sandy") return <SandySection key={s.id} s={s} track={track} />;
        if (s.type === "claim_path") return <ClaimPath key={s.id} s={s} track={track} />;
        if (s.type === "cta") return <FinalCta key={s.id} s={s} track={track} />;
        if (s.type === "lead_form") return page.lead_form_enabled ? <LeadForm key={s.id} s={s} slug={slug} track={track} /> : null;
        const fn = RENDER[s.type];
        return fn ? <div key={s.id}>{fn(s)}</div> : null;
      })}
      {page.hero?.cta_primary?.label && (
        <div className="fixed bottom-0 inset-x-0 z-40 sm:hidden bg-[#0a0a0a]/90 backdrop-blur border-t border-white/10 px-4 py-3" data-testid="sticky-mobile-cta">
          <CtaLink btn={page.hero.cta_primary} track={track}
            className="block bg-rose-500 hover:bg-rose-600 text-white font-bold text-center py-3 rounded-full text-sm" testid="sticky-cta-btn" />
        </div>
      )}
      <div className="h-16 sm:hidden" />
    </div>
  );
}
